import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BREAKTHROUGH_EVALUATION_DAYS } from '@cortex/domain-ac-mens-hub-api';
import { breakthroughApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import { periodKey } from '../lib/kpi-aggregation.js';
import type { Granularity } from '../lib/kpi-aggregation.js';
import type {
  AggregatedBreakthroughRow,
  BreakthroughEvaluationDays,
  BreakthroughRecord,
} from '../types/breakthrough.js';

/**
 * 粒度別の表示ラベルを生成する。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business periodKey の値を粒度に応じた表示用文字列に変換する
 * @graph-connects none
 */
export function formatPeriodLabel(key: string, granularity: Granularity): string {
  return granularity === 'daily' || granularity === 'weekly' ? key.slice(5) : key;
}

/**
 * 経過日数が評価期間に達した突破月だけ率を返す。未達なら null にして線を途切れさせる。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 評価期間を経過した突破月のみ率を算出し、未経過や母数0は null にする
 * @graph-connects none
 */
function rateIfConfirmed(
  count: number,
  cohortCount: number,
  elapsedDays: number,
  requiredDays: number,
): number | null {
  if (cohortCount <= 0) return null;
  if (elapsedDays < requiredDays) return null;
  return count / cohortCount;
}

/**
 * 日次コホートデータを指定粒度で集約する。全レコードをバケット化し、バケット内の最新コホート日の
 * 経過日数で突破月ごとに確定判定する。バケット自体の表示可否は最短の評価期間（1ヶ月目）で決まり、
 * 2・3ヶ月目は未経過ならその系列の率だけ null になる。
 * 週次の起点は月曜（kpi-aggregation.ts の periodKey(weekly) と同一）。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次コホートデータを任意粒度に集約し、突破月ごとに確定状態をバケット単位で判定する。
 * @graph-connects periodKey [calls] 共通粒度キー生成
 */
export function aggregateByGranularity(
  records: BreakthroughRecord[],
  evaluationDays: BreakthroughEvaluationDays,
  granularity: Granularity,
): AggregatedBreakthroughRow[] {
  const todayMs = Date.now();

  const bucketMap = new Map<
    string,
    {
      cohortCount: number;
      m1: number;
      m2: number;
      m3: number;
      maxCohortDate: string;
    }
  >();

  for (const r of records) {
    const key = granularity === 'daily' ? r.cohortDate : periodKey(r.cohortDate, granularity);
    const entry = bucketMap.get(key) ?? {
      cohortCount: 0,
      m1: 0,
      m2: 0,
      m3: 0,
      maxCohortDate: '',
    };
    entry.cohortCount += r.cohortCount;
    entry.m1 += r.breakthroughCountM1;
    entry.m2 += r.breakthroughCountM2;
    entry.m3 += r.breakthroughCountM3;
    if (r.cohortDate > entry.maxCohortDate) entry.maxCohortDate = r.cohortDate;
    bucketMap.set(key, entry);
  }

  const rows: AggregatedBreakthroughRow[] = [];
  for (const [key, entry] of bucketMap) {
    const latestCohortMs = new Date(`${entry.maxCohortDate}T00:00:00Z`).getTime();
    const elapsedDays = Math.floor((todayMs - latestCohortMs) / (24 * 60 * 60 * 1000));
    rows.push({
      periodKey: key,
      displayLabel: formatPeriodLabel(key, granularity),
      cohortCount: entry.cohortCount,
      breakthroughCountM1: entry.m1,
      breakthroughCountM2: entry.m2,
      breakthroughCountM3: entry.m3,
      breakthroughRateM1: rateIfConfirmed(
        entry.m1,
        entry.cohortCount,
        elapsedDays,
        evaluationDays.m1,
      ),
      breakthroughRateM2: rateIfConfirmed(
        entry.m2,
        entry.cohortCount,
        elapsedDays,
        evaluationDays.m2,
      ),
      breakthroughRateM3: rateIfConfirmed(
        entry.m3,
        entry.cohortCount,
        elapsedDays,
        evaluationDays.m3,
      ),
      isConfirmed: elapsedDays >= evaluationDays.m1,
    });
  }

  return rows.sort((a, b) => a.periodKey.localeCompare(b.periodKey));
}

/**
 * 突破率データを取得し指定粒度で集計済みデータを返すフック。1ヶ月目の評価期間が未経過のバケットは
 * 確定値でないため返さない。2・3ヶ月目は評価期間が未経過なら率が null になり線が途切れる。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 1〜3ヶ月目突破率のコホート別集計データを取得し、指定粒度で集約するフック。1ヶ月目が未確定のバケットを除外し確定値のみ返す。結果はメモ化される。
 * @graph-connects breakthroughApi [calls] breakthrough API client
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useBreakthrough(from?: string, to?: string, granularity: Granularity = 'weekly') {
  const query = useQuery({
    queryKey: queryKeys.breakthrough.records(from, to),
    queryFn: () => breakthroughApi.getRecords({ from, to }),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const evaluationDays = query.data?.evaluationDays ?? BREAKTHROUGH_EVALUATION_DAYS;

  const confirmedRows = useMemo(() => {
    if (!query.data) return [];
    return aggregateByGranularity(query.data.rows, query.data.evaluationDays, granularity).filter(
      (row) => row.isConfirmed,
    );
  }, [query.data, granularity]);

  return {
    ...query,
    evaluationDays,
    confirmedRows,
  };
}
